import { API, Hub } from 'aws-amplify';
import * as queries from '../graphql/queries';

/**
 * Which Cognito sub is this app's data actually keyed on for the signed-in person?
 *
 * Normally that is the sub on their token. During the FITTWorks SSO migration it is not: the
 * UserMigration trigger recreates each user in the hub pool with their password intact but CANNOT
 * preserve the sub, and the sub is the de-facto foreign key across the LeanFITT model — no field
 * anywhere holds a User.id, so `Organization.owner`, `OrganizationMember.userSub`, reports, action
 * items, awards, coins and learning progress all store the raw legacy sub.
 *
 * Between a person's first hub sign-in and the remap worker rewriting their rows, the token carries
 * a sub that matches nothing. Two things broke on that window, both silently and both on the mount
 * path: the sync checkpoint missed and created a duplicate User row with `termsAccepted: false`, and
 * OrganizationContext resolved no organizations and offered to create a first one — showing a
 * migrated owner an empty account and inviting a duplicate org on a duplicate licence.
 *
 * The join needed to fix that is already in our own data: a migrated person's User row still carries
 * their legacy sub in `cognitoID`, and email reaches it. So resolve identity here, once, and let
 * every caller key off the result.
 *
 * Deliberately does NOT re-key `User.cognitoID` to the token's sub. That would move the User row to
 * the new sub while every organization, membership and report row still held the old one, which is
 * the empty-account failure this exists to prevent. Only the remap rewrites, and it rewrites the
 * whole set. That also makes step 1 a convergence signal: once a lookup by token sub hits, the remap
 * has run and the fallback below stops firing on its own.
 */

// Caches the DECISION (which sub, and whether they are migrated) — never the row. The decision is
// what is expensive to reach and what is stable for a session; the row is neither. Caching the row
// froze `termsAccepted` and the avatar path then wrote the stale value back, reverting a real
// acceptance. Caching a MISS was worse: the row a caller was about to create stayed invisible to the
// next caller, so a single new signup produced two or three duplicate User rows — the exact defect
// this module exists to prevent.
const decided = new Map();

// Every sign-out, including paths this module has never heard of. Amplify fires this for
// Auth.signOut() wherever it is called, which is what the previous single hook in UserContext missed.
Hub.listen('auth', ({ payload }) => {
  if (payload?.event === 'signOut') decided.clear();
});

const live = (items) => (items || []).filter((u) => !u._deleted);

// 45 emails in production carry more than one User row (up to nine), so "the row for this email" has
// to be deterministic. Earliest-created is the rule the migration spec uses when merging ghost rows,
// and 44 of those 45 clusters share a single cognitoID, so for all but one the choice cannot change
// the answer. That one is a genuine two-identity case and needs a human merge.
const earliest = (rows) =>
  rows.reduce((oldest, row) => (!oldest || row.createdAt < oldest.createdAt ? row : oldest), null);

const findByCognitoID = async (cognitoID) => {
  const response = await API.graphql({
    query: queries.usersByCognitoID,
    variables: { cognitoID },
    fetchPolicy: 'network-only',
  });
  return live(response.data.usersByCognitoID.items);
};

const findByEmail = async (email) => {
  // There is no index on email, so this is a filtered Scan — and DynamoDB applies `limit` BEFORE
  // `filter`, so one page can come back empty while a match sits further along. Page to exhaustion
  // rather than trusting the first response.
  // ponytail: a Scan is fine here — the table is ~260 rows, and this only runs on the miss path,
  // once per session. If it grows past a few thousand, add a byEmail GSI; that is a schema change,
  // so not while the pool migration is in flight.
  let items = [];
  let nextToken = null;
  do {
    const response = await API.graphql({
      query: queries.listUsers,
      variables: { filter: { email: { eq: email } }, limit: 1000, nextToken },
      fetchPolicy: 'network-only',
    });
    const page = response.data.listUsers;
    items = items.concat(page.items || []);
    nextToken = page.nextToken;
  } while (nextToken);
  return live(items);
};

/**
 * @param {Object} cognitoUser - from Auth.currentAuthenticatedUser()
 * @returns {Promise<{sub: string, user: Object|null, migrated: boolean}>}
 *   sub      - the sub this app's data is keyed on; use it for every ownership/membership query
 *   user     - their existing User row, or null if they are new to LeanFITT
 *   migrated - true when the token's sub and the data's sub differ (migrated, not yet remapped)
 */
export const resolveAppSub = async (cognitoUser) => {
  const tokenSub = cognitoUser?.attributes?.sub;
  const email = cognitoUser?.attributes?.email;

  if (!tokenSub) {
    throw new Error('resolveAppSub: no sub on the authenticated user');
  }

  // Decision already made this session — re-read the row, which mutates, on the sub, which does not.
  // Costs the same single GSI query the callers each made before this module existed.
  //
  // The row is selected by the SAME rule that established the decision, because the two rules
  // disagree whenever a sub carries duplicates — and 46 of 155 production cognitoIDs do, up to six
  // rows for one person with `termsAccepted` differing between them. Picking earliest on the first
  // call and first-live on every call after it would flip a user's row mid-session, which reads as
  // terms un-accepting themselves or a profile reverting.
  const previous = decided.get(tokenSub);
  if (previous) {
    const rows = await findByCognitoID(previous.sub);
    if (rows.length) {
      return { ...previous, user: previous.migrated ? earliest(rows) : rows[0] };
    }
    // Empty on a sub that hit before means the world moved underneath the decision: the remap has
    // rewritten User.cognitoID to the token's sub, so the legacy sub now matches nothing. Returning
    // `user: null` here would be read by syncUserWithDatabase as "new user" and answered by creating
    // a DUPLICATE row with termsAccepted false — the exact ghost row this module exists to prevent,
    // recreated by its own cache, on every fetchUser for the rest of the session.
    //
    // So drop the decision and re-resolve from scratch. Step 1 then hits on the token sub and the
    // person converges to migrated:false, which is the correct end state.
    decided.delete(tokenSub);
  }

  // 1. Native to this pool, or already remapped — the token's sub is the data's sub.
  //
  // Takes the FIRST live row, not the earliest, deliberately: 46 of 155 production cognitoIDs appear
  // on more than one User row, and first-live is what the previous callers picked. Switching those
  // ~30% of people to a different row of their own duplicate set is a behaviour change on a path
  // that works today — it could re-prompt terms for anyone whose later row carries the acceptance.
  // Earliest-wins is applied only to the email fallback below, which is new behaviour regardless.
  const direct = await findByCognitoID(tokenSub);
  if (direct.length) {
    decided.set(tokenSub, { sub: tokenSub, migrated: false });
    return { sub: tokenSub, user: direct[0], migrated: false };
  }

  // 2. Migrated, not yet remapped — their row still carries the legacy sub.
  //
  // Gated on a verified email. Matching an unverified address to an existing row would let anyone
  // who can sign up claim another person's data, and the hub pool's self-signup is open. Cognito
  // reports this as a boolean or the string "true" depending on the path, so accept both.
  const emailVerified =
    cognitoUser.attributes.email_verified === true || cognitoUser.attributes.email_verified === 'true';
  const sameEmail = email && emailVerified ? await findByEmail(email) : [];
  if (sameEmail.length) {
    const row = earliest(sameEmail);
    console.warn(
      `[appIdentity] ${email} signed in with a sub this data does not use yet; ` +
        'keying on the legacy sub until the remap completes',
    );
    decided.set(tokenSub, { sub: row.cognitoID, migrated: true });
    return { sub: row.cognitoID, user: row, migrated: true };
  }

  // 3. New to LeanFITT. Their data starts on the token's sub, which is correct.
  //
  // Deliberately NOT remembered. The caller's next move is to create this person's row, and a
  // remembered miss would hide it from the following caller, who would create a second one.
  return { sub: tokenSub, user: null, migrated: false };
};

/**
 * Does a LeanFITT User row already exist for this email address? Case-insensitive.
 *
 * The guard that stops InviteAccept minting a hub account (Auth.signUp) for someone who already has a
 * legacy LeanFITT account. That signup creates an UNCONFIRMED shadow in the hub pool which then blocks
 * BOTH sign-in migration and password reset for their real, CONFIRMED account — stranding them for
 * good, and exactly how the first shadow was created. On a match the caller must route to password
 * reset, which fires UserMigration_ForgotPassword and brings the legacy account's data across, rather
 * than signing up.
 *
 * Compared in JS rather than a server-side `eq`: production User rows carry mixed-case emails (see
 * InviteAccept.findMember), so an exact filter would miss a real row and defeat the guard. Fail
 * closed — the one caller treats a thrown error as "do not sign up".
 *
 * ponytail: full Scan of the User table, fine at ~260 rows and only on an explicit button click. Add
 * a byEmail GSI if the table grows into the thousands — a schema change, so not while the pool
 * migration is in flight.
 */
export const userExistsByEmail = async (email) => {
  const needle = email?.toLowerCase().trim();
  if (!needle) return false;

  let nextToken = null;
  do {
    const response = await API.graphql({
      query: queries.listUsers,
      variables: { limit: 1000, nextToken },
      fetchPolicy: 'network-only',
    });
    const page = response.data.listUsers;
    if (live(page.items).some((u) => u.email?.toLowerCase().trim() === needle)) return true;
    nextToken = page.nextToken;
  } while (nextToken);

  return false;
};

/** Drop the decisions. Wired to Hub's signOut above; exported for tests and explicit resets. */
export const clearAppIdentity = () => decided.clear();
